import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Header = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { href: "/nos-services", label: "Services", anchor: "#services" },
    { href: "/nos-formules", label: "Formules", anchor: "#formules" },
    { href: "/#devis", label: "Devis", anchor: "#devis" },
    { href: "/contact", label: "Contact", anchor: "#contact" },
  ];

  const handleNavClick = (link) => {
    setMobileMenuOpen(false);
    
    // If we're on the homepage and it's an anchor link
    if (location.pathname === "/" && link.anchor) {
      const element = document.querySelector(link.anchor);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    
    // If it's a page link or we need to navigate
    if (link.href.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(link.anchor);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      navigate(link.href);
    }
  };

  const scrollToDevis = () => {
    setMobileMenuOpen(false);
    navigate("/contact");
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          isScrolled ? "shadow-lg" : ""
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3" data-testid="logo-link">
              <img
                src="/assets/logo-transfast.webp"
                alt="TRANSFAST DÉMÉNAGEMENT"
                className="h-16 w-auto"
                width="384"
                height="257"
                decoding="async"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="font-semibold text-sm uppercase tracking-wide transition-colors text-slate-700 hover:text-[#FF6B00]"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Phone & CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:0182836365"
                className="flex items-center gap-2 font-semibold text-[#004E98]"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" />
                <span>01 82 83 63 65</span>
              </a>
              <Button
                onClick={scrollToDevis}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white font-bold uppercase tracking-wide px-6"
                data-testid="cta-devis-header"
              >
                Devis gratuit
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-900" />
              ) : (
                <Menu className="w-6 h-6 text-slate-900" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-white z-[200] transform transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <img
              src="/assets/logo-transfast.webp"
              alt="TRANSFAST DÉMÉNAGEMENT"
              className="h-12 w-auto"
              width="384"
              height="257"
              decoding="async"
            />
            <button onClick={() => setMobileMenuOpen(false)} data-testid="close-mobile-menu">
              <X className="w-6 h-6 text-slate-900" />
            </button>
          </div>
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="text-left text-xl font-bold text-slate-900 py-3 border-b border-slate-100"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </nav>
          <div className="mt-8 space-y-4">
            <a
              href="tel:0182836365"
              className="flex items-center gap-3 text-[#004E98] font-bold text-lg"
            >
              <Phone className="w-5 h-5" />
              01 82 83 63 65
            </a>
            <Button
              onClick={scrollToDevis}
              className="w-full bg-[#FF6B00] hover:bg-[#e55f00] text-white font-bold uppercase py-4"
            >
              Demander un devis gratuit
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
